import { useRouter } from "next/router";
import { VFC, useEffect, useRef } from "react";

/**
 * Parses UTM parameters from the current URL.
 */
function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utms: Record<string, string> = {};
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  for (const key of keys) {
    const value = params.get(key);
    if (value) {
      utms[key] = value;
    }
  }
  return utms;
}

/**
 * Extracts the domain from a referrer URL. Returns empty string if same-site or empty.
 */
function getReferrerDomain(): string {
  try {
    if (!document.referrer) return "";
    const url = new URL(document.referrer);
    if (url.hostname === window.location.hostname) return "";
    return url.hostname;
  } catch {
    return "";
  }
}

/**
 * Derives the product area from the URL path.
 * e.g., /docs/customer-studio/audiences → "customer-studio"
 */
function getProductArea(): string {
  const path = window.location.pathname.replace(/^\/docs\/?/, "");
  const segment = path.split("/")[0];
  return segment || "home";
}

export const HtEvents: VFC = () => {
  const router = useRouter();
  const isFirstPageView = useRef(true);

  const load = async () => {
    const { HtEventsBrowser } = await import("@ht-sdks/events-sdk-js-browser");
    window.htevents = HtEventsBrowser.load(
      { writeKey: process.env.NEXT_PUBLIC_HT_EVENTS_WRITE_KEY },
      {
        apiHost: process.env.NEXT_PUBLIC_HT_EVENTS_DATA_PLANE_URL,
        batching: false,
      }
    );
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      if (window.htevents) {
        const properties: Record<string, unknown> = {
          product_area: getProductArea(),
          ...getUtmParams(),
        };

        const referrerDomain = getReferrerDomain();
        if (referrerDomain) {
          properties.referrer_domain = referrerDomain;
        }

        if (isFirstPageView.current) {
          properties.is_entry_page = true;
          isFirstPageView.current = false;
        }

        window.htevents.page(undefined, undefined, properties);
      }
    };

    handleRouteChange();
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router]);

  return null;
};
